import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import {
    Newspaper, Trash2, Edit, ChevronLeft,
    Calendar, Image as ImageIcon, Plus, Save, AlignLeft
} from "lucide-react";
// DODAJ IMPORTY:
import { useSearchParams } from "react-router-dom";
// ==========================================
// STYLED COMPONENTS (WSPÓŁDZIELONE Z ROCZNIKIEM)
// ==========================================

const ManagerContainer = styled.div`
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;

  h2 {
    margin: 0;
    font-size: 1.8rem;
    color: #111;
    font-weight: 800;
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #111;
  color: #fff;
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;

  &:hover { background: #333; transform: translateY(-2px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  color: #111;
  padding: 10px 18px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover { background: #f4f6f8; }
`;

// --- KARTY FORMULARZY ---

const FormCard = styled.form`
  background: #fff;
  border-radius: 12px;
  padding: 30px;
  border: 1px solid #eee;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
  margin-bottom: 30px;
`;

const FormSectionTitle = styled.h4`
  margin: 0 0 20px 0;
  font-size: 1.1rem;
  color: #111;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  svg { color: #888; }
`;

const FormGroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label { font-size: 0.9rem; font-weight: 700; color: #555; }
  input, textarea {
    padding: 12px 14px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    font-family: inherit;
    transition: border-color 0.3s;
    &:focus { outline: none; border-color: #111; }
  }
  
  textarea {
    min-height: 150px;
    resize: vertical;
    line-height: 1.6;
  }
`;

// --- WIDOK LISTY (SIATKA KAFELKÓW) ---

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
`;

const NewsCard = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #eee;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .image-wrapper {
    width: 100%;
    height: 180px;
    background: #f4f6f8;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    svg {
      color: #ccc;
      width: 40px;
      height: 40px;
    }
  }

  .content {
    padding: 20px;
    display: flex;
    flex-direction: column;
    flex: 1;

    .date {
      font-size: 0.8rem;
      color: #999;
      font-weight: 700;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 5px;
    }

    h3 {
      margin: 0 0 10px 0;
      font-size: 1.2rem;
      font-weight: 800;
      color: #111;
      line-height: 1.3;
    }

    p {
      margin: 0;
      color: #666;
      font-size: 0.95rem;
      line-height: 1.5;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .actions {
    display: flex;
    gap: 10px;
    padding: 15px 20px;
    border-top: 1px solid #f0f0f0;
    background: #fafafa;

    button {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 5px;
      padding: 10px;
      border: none;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
      transition: 0.2s;
    }

    .btn-edit { background: #eef2ff; color: #234ffc; }
    .btn-edit:hover { background: #dce5ff; }

    .btn-delete { background: #fff0f0; color: #dc3545; }
    .btn-delete:hover { background: #ffe0e0; }
  }
`;
// --- FUNKCJA POMOCNICZA DO DAT ---
const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    // Jeśli to już jest YYYY-MM-DD, po prostu to zwróć
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    // Jeśli to stary format DD-MM-YYYY (np. 20-04-2026), odwróć go
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split('-');
        return `${year}-${month}-${day}`;
    }
    // Próba awaryjnego formatowania innych dat
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];

    return "";
};
// ==========================================
// GŁÓWNY KOMPONENT
// ==========================================
// W GŁÓWNEJ FUNKCJI NewsManager:
export default function NewsManager() {
    const [news, setNews] = useState([]);

    // URL Params
    const [searchParams, setSearchParams] = useSearchParams();
    const viewState = searchParams.get("action") || "list";
    const editingId = searchParams.get("id") || null;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({ tytul: "", data: "", tresc: "", zdjecie: "" });
    const [imageFile, setImageFile] = useState(null);
    const dataLoadedRef = useRef(null);

    // (Tu zostawiasz fetchNews i useEffect pobierający dane na start)
    const fetchNews = async () => {
        try {
            const res = await fetch("https://api.pth-hokej.pl/api/aktualnosci");
            const data = await res.json();
            setNews(data);
        } catch (err) {
            console.error("Błąd pobierania aktualności:", err);
        }
    };

    useEffect(() => {
        fetchNews();
    }, []);

    // AUTO-WYPEŁNIANIE PO ODŚWIEŻENIU (F5)
    useEffect(() => {
        if (viewState === "edit" && editingId && news.length > 0) {
            if (dataLoadedRef.current !== editingId) {
                const item = news.find(n => n._id === editingId);
                if (item) {
                    setFormData({
                        tytul: item.tytul || "",
                        data: formatDateForInput(item.data), // <--- TUTAJ ZMIANA
                        tresc: item.tresc || "",
                        zdjecie: item.zdjecie || ""
                    });
                    setImageFile(null);
                    dataLoadedRef.current = editingId;
                }
            }
        } else if (viewState === "list") {
            dataLoadedRef.current = null;
        }
    }, [viewState, editingId, news]);

    const handleAddClick = () => {
        // Generujemy dzisiejszą datę od razu w formacie YYYY-MM-DD
        const today = new Date().toISOString().split('T')[0];
        setFormData({ tytul: "", data: today, tresc: "", zdjecie: "" });
        setImageFile(null);
        setSearchParams({ action: "add" });
    };

    const handleEditClick = (item) => {
        setFormData({
            tytul: item.tytul || "",
            data: formatDateForInput(item.data), // <--- TUTAJ ZMIANA
            tresc: item.tresc || "",
            zdjecie: item.zdjecie || ""
        });
        setImageFile(null);
        dataLoadedRef.current = item._id;
        setSearchParams({ action: "edit", id: item._id });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const handleBackToList = () => setSearchParams({});
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const url = viewState === "edit" ? `https://api.pth-hokej.pl/api/aktualnosci/${editingId}` : "https://api.pth-hokej.pl/api/aktualnosci";
            const method = viewState === "edit" ? "PUT" : "POST";

            // Używamy FormData do wysyłki plików
            const submitData = new FormData();
            submitData.append("tytul", formData.tytul);
            submitData.append("data", formData.data);
            submitData.append("tresc", formData.tresc);

            // Dodajemy plik lub sam link
            if (imageFile) {
                submitData.append("zdjeciePlik", imageFile);
            } else if (formData.zdjecie) {
                submitData.append("zdjecie", formData.zdjecie);
            }

            await fetch(url, {
                method,
                body: submitData,
            });

            await fetchNews();
            setSearchParams({}); // Wraca do listy po zapisie
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Na pewno chcesz bezpowrotnie usunąć ten wpis?")) return;
        try {
            await fetch(`https://api.pth-hokej.pl/api/aktualnosci/${id}`, { method: "DELETE" });
            fetchNews();
        } catch (err) {
            console.error(err);
        }
    };
    return (
        <ManagerContainer>

            {/* WIDOK LISTY AKTUALNOŚCI */}
            {viewState === "list" && (
                <>
                    <HeaderRow>
                        <h2>Zarządzanie Aktualnościami</h2>
                        <PrimaryButton onClick={handleAddClick}>
                            <Plus size={20} /> Nowy Wpis
                        </PrimaryButton>
                    </HeaderRow>

                    {news.length === 0 ? (
                        <p style={{ color: "#666" }}>Brak dodanych aktualności na blogu.</p>
                    ) : (
                        <NewsGrid>
                            {news.map((item) => (
                                <NewsCard key={item._id}>
                                    <div className="image-wrapper">
                                        {item.zdjecie ? (
                                            <img src={item.zdjecie} alt={item.tytul} />
                                        ) : (
                                            <Newspaper />
                                        )}
                                    </div>
                                    <div className="content">
                                        <div className="date"><Calendar size={14} /> {item.data}</div>
                                        <h3>{item.tytul}</h3>
                                        <p>{item.tresc}</p>
                                    </div>
                                    <div className="actions">
                                        <button className="btn-edit" onClick={() => handleEditClick(item)}>
                                            <Edit size={16} /> Edytuj
                                        </button>
                                        <button className="btn-delete" onClick={() => handleDelete(item._id)}>
                                            <Trash2 size={16} /> Usuń
                                        </button>
                                    </div>
                                </NewsCard>
                            ))}
                        </NewsGrid>
                    )}
                </>
            )}

            {/* WIDOK FORMULARZA (DODAWANIE / EDYCJA) */}
            {(viewState === "add" || viewState === "edit") && (
                <>
                    <HeaderRow>
                        <SecondaryButton onClick={handleBackToList}>
                            <ChevronLeft size={20} /> Wróć do listy
                        </SecondaryButton>
                        <h2>{viewState === "edit" ? "Edycja Wpisu" : "Nowa Aktualność"}</h2>
                    </HeaderRow>

                    <FormCard onSubmit={handleSubmit}>

                        <FormSectionTitle><AlignLeft size={20} /> Treść wpisu</FormSectionTitle>
                        <FormGroupGrid>
                            <FormGroup style={{ flex: 2 }}>
                                <label>Tytuł aktualności</label>
                                <input required name="tytul" placeholder="Wpisz chwytliwy tytuł..." value={formData.tytul} onChange={handleChange} />
                            </FormGroup>
                            <FormGroup style={{ flex: 1 }}>
                                <label>Data publikacji</label>
                                <input
                                    required
                                    type="date"
                                    name="data"
                                    value={formData.data}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </FormGroupGrid>

                        <FormGroup style={{ marginBottom: '30px' }}>
                            <label>Główna treść</label>
                            <textarea
                                required
                                name="tresc"
                                placeholder="Rozpisz się! Ten tekst pojawi się po rozwinięciu na stronie głównej oraz w zakładce Aktualności..."
                                value={formData.tresc}
                                onChange={handleChange}
                            />
                        </FormGroup>

                        <FormSectionTitle><ImageIcon size={20} /> Zdjęcie w tle</FormSectionTitle>
                        <FormGroup style={{ background: '#fafafa', padding: '20px', borderRadius: '8px', border: '1px solid #eee' }}>
                            <label style={{ marginBottom: '10px' }}>Wybierz źródło zdjęcia (jedna z opcji)</label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', alignItems: 'center' }}>

                                <div>
                                    <span style={{ fontSize: '0.85rem', color: '#888', display: 'block', marginBottom: '8px' }}>Opcja 1: Wgraj zdjęcie z komputera</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setImageFile(e.target.files[0])}
                                        style={{ padding: '8px', background: '#fff', width: '100%', boxSizing: 'border-box' }}
                                        disabled={!!formData.zdjecie}
                                    />
                                </div>

                                <div>
                                    <span style={{ fontSize: '0.85rem', color: '#888', display: 'block', marginBottom: '8px' }}>Opcja 2: Wklej link do zdjęcia z internetu</span>
                                    <input
                                        name="zdjecie"
                                        value={formData.zdjecie}
                                        onChange={handleChange}
                                        placeholder="https://..."
                                        disabled={!!imageFile}
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </div>
                            </div>
                        </FormGroup>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                            <PrimaryButton type="submit" disabled={loading}>
                                <Save size={20} /> {viewState === "edit" ? "Zapisz Zmiany" : "Opublikuj Aktualność"}
                            </PrimaryButton>
                        </div>
                    </FormCard>
                </>
            )}
        </ManagerContainer>
    );
}